import { useContext, useEffect, useRef, useState } from "react"
import { MdPhone, MdShoppingCart } from "react-icons/md"
import { AppContext } from "../App"
import { callApi, formatMoney, getInlineLoader, normalizePhoneNumber } from "../Helpers"
import AccountLogin from "../views/AccountLogin"


export default function PurchasePanel({ data, type }) {

  const appContext = useContext(AppContext);
  const appContextRef = useRef(appContext);
  const [loading, setLoading] = useState(false);
  const [payPhone, setPayPhone] = useState('');
  const [paymentRequested, setPaymentRequested] = useState(false);
  const [paymentOrderUuid, setPaymentOrderUuid] = useState(null);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');

  useEffect(() => {
    appContextRef.current = appContext;
  }, [appContext])

  useEffect(() => {
    if (appContext.currentUserData?.phone) {
      setPayPhone(appContext.currentUserData.phone);
    }
  }, [appContext.currentUserData])

  useEffect(() => {
    if (!paymentRequested || !paymentOrderUuid) {
      return;
    }

    let active = true;
    let attempts = 0;
    let timeoutId = null;

    async function syncPayment() {
      if (!active) {
        return;
      }

      attempts++;

      await callApi('sync-order-payment', {
        orderUuid: paymentOrderUuid,
      }).then((response) => {
        if (!active) {
          return;
        }

        if (response.status === 1 && response.data?.status === 'paid') {
          active = false;
          setPaymentStatusMessage('Payment confirmed successfully.');
          appContextRef.current.tellMessage('Payment confirmed successfully.');
          appContextRef.current.navTo({
            item: 'view',
            subItem: 'view-order',
            extraItem: paymentOrderUuid,
          })
        } else if (response.status === 1 && response.data?.status === 'cancelled') {
          active = false;
          setPaymentStatusMessage('This payment was not completed.');
          appContextRef.current.tellError('This payment was not completed.');
        } else if (attempts < 15) {
          setPaymentStatusMessage('Waiting for payment confirmation...');
          timeoutId = setTimeout(syncPayment, 8000);
        } else {
          active = false;
          setPaymentStatusMessage('Confirmation is taking longer than expected. Check your order history shortly.');
          appContextRef.current.tellInfo('Confirmation is taking longer than expected. Check your order history shortly.');
        }
      })
    }

    setPaymentStatusMessage('Waiting for payment confirmation...');
    timeoutId = setTimeout(syncPayment, 8000);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    }
  }, [paymentRequested, paymentOrderUuid])

  async function initPayment() {
    const normalizedPhone = normalizePhoneNumber(payPhone);

    if (!normalizedPhone) {
      appContext.tellError('Invalid payment phone number');
      return;
    }

    setPayPhone(normalizedPhone);
    setLoading(true);
    await callApi('init-payment', {
      itemType: type.toLowerCase(),
      itemUuid: data.uuid,
      payPhone: normalizedPhone,
    }).then((response) => {
      if (response.status === 1) {
        setPaymentOrderUuid(response.data?.orderUuid || null);
        setPaymentRequested(true);
        setPaymentStatusMessage('Waiting for payment confirmation...');
        appContext.tellMessage(response.msg || 'Check your phone to complete payment');
      } else {
        appContext.tellError(response.msg || 'Could not initiate payment');
      }
    })
    setLoading(false);
  }

  if (!appContext.auth) {
    return (
      <div className="marketplace-purchase-login">
        <p className="mb-3">Sign in or create an account to purchase this {type.toLowerCase()}.</p>
        <AccountLogin onLogin={appContext.refresh} />
      </div>
    )
  }

  return (
    <>
      <p>Enter the mobile money number you want to use to pay.</p>
      <div className="input-group marketplace-payment-phone">
        <span className="input-group-text"><MdPhone className="text-primary" size={21}/></span>
        <input
          className="form-control"
          type="tel"
          value={payPhone}
          onChange={(event) => setPayPhone(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && initPayment()}
          placeholder="Payment phone number"
          disabled={loading}
        />
      </div>
      <button
        className="btn btn-primary btn-rounded w-100 marketplace-payment-button"
        onClick={initPayment}
        disabled={loading || paymentRequested}
      >
        {
          loading
            ? getInlineLoader()
            : paymentRequested
              ? 'Payment Request Sent'
              : <><MdShoppingCart size={21}/> Pay TZS {formatMoney(data.price)}</>
        }
      </button>
      {
        paymentStatusMessage ?
          <small className="d-block mt-3 text-center">{paymentStatusMessage}</small>
          : ""
      }
    </>
  )
}
